/**
 * Motivo di un invio fallito su un numero "Ufficiale", in italiano semplice.
 *
 * Il webhook meta-whatsapp-webhook salva l'errore di Meta in
 * `whatsapp_messages.metadata.error = { code, title, message, details, href }`.
 * Questo modulo lo traduce in una frase per l'operatore (da mostrare sotto la
 * bolla del messaggio non inviato).
 *
 * Codici dalla documentazione Meta "Error codes" (Cloud API, settembre 2026).
 * Modulo puro, testato in __tests__/cloudErrorMessages.test.ts.
 */

const MESSAGES: Record<number, string> = {
  131047: "Sono passate piu' di 24 ore dall'ultimo messaggio del cliente: puoi scrivergli solo con un modello approvato.",
  131026: 'Messaggio non consegnabile: il numero potrebbe non avere WhatsApp o usare una versione troppo vecchia.',
  131051: 'Tipo di messaggio non supportato da WhatsApp.',
  131052: "Non e' stato possibile scaricare l'allegato del cliente.",
  131053: "Caricamento dell'allegato non riuscito: controlla il formato del file.",
  131056: 'Troppi messaggi allo stesso cliente in poco tempo: aspetta qualche minuto e riprova.',
  131049: 'Meta ha bloccato il messaggio per limitare i messaggi promozionali a questo cliente. Riprova tra almeno 24 ore.',
  131048: 'Troppi messaggi di questo numero sono stati segnalati: invii limitati da Meta.',
  131021: 'Non puoi scrivere al numero da cui stai inviando.',
  131031: "L'account WhatsApp e' limitato da Meta.",
  131042: 'Problema con il metodo di pagamento dell\'account WhatsApp.',
  131000: 'Errore di consegna sconosciuto: riprova.',
  131009: 'Parametro non valido: la reazione o il messaggio di riferimento non sono piu\' disponibili.',
  132000: 'Il numero di variabili non corrisponde al modello approvato.',
  132001: 'Modello inesistente in questa lingua o non ancora approvato.',
  132012: 'Le variabili del modello non sono nel formato richiesto.',
  132015: "Modello in pausa per bassa qualita'.",
  132016: 'Modello disattivato definitivamente da Meta: creane uno nuovo.',
  130472: 'Messaggio escluso da un esperimento di Meta.',
  130429: 'Limite di invio raggiunto: riprova tra poco.',
};

/** Frase italiana per un codice di errore Meta. */
export function describeCloudError(code: number | string | null | undefined, fallback?: string | null): string {
  const n = typeof code === 'number' ? code : Number(code);
  if (Number.isFinite(n) && MESSAGES[n]) return MESSAGES[n];
  const tail = fallback?.trim();
  return tail ? `Invio non riuscito: ${tail}` : 'Invio non riuscito.';
}

/**
 * Motivo del fallimento letto da `whatsapp_messages.metadata`, oppure null se
 * il messaggio non ha un errore salvato.
 */
export function cloudFailureReason(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const err = (metadata as Record<string, unknown>).error;
  if (!err || typeof err !== 'object') return null;
  const e = err as Record<string, unknown>;
  const fallback = (typeof e.details === 'string' && e.details) || (typeof e.title === 'string' && e.title) || null;
  return describeCloudError(e.code as number | string | null, fallback);
}
